import type { PatientAdminApiRouterOutputs } from '@liza/patient-admin-api';
import type { LabScaleType } from '@liza/ui/components/records/AddLabResultForm';

/**
 * One property entry from `codeSystem.lookup` — `{ value, description? }` for a single value
 * or an array of those when the property repeats. Inferred from the tRPC output so the type
 * stays in sync with the API without depending on `@liza/services` directly.
 */
type LookupPropertyValue = PatientAdminApiRouterOutputs['codeSystem']['lookup']['properties'][string];

const LOINC_SCALE_TO_FORM_SCALE: Record<string, LabScaleType> = {
  Qn: 'Qn',
  Qual: 'Ord',
  Ord: 'Ord',
  Nom: 'Ord',
  Nar: 'Nar',
};

/**
 * Each property entry carries `{ value, description? }`; we only care about the value here.
 * Repeated properties collapse into an array (see `parseLookupResponse`), so we return
 * `string | string[] | undefined` matching the shape the rest of this module expects.
 */
const unwrap = (raw: LookupPropertyValue | undefined): string | string[] | undefined => {
  if (raw === undefined) return undefined;
  return Array.isArray(raw) ? raw.map((p) => p.value) : raw.value;
};

const parseOrdinalChoices = (value: string | string[] | undefined): string[] | undefined => {
  if (!value) return undefined;
  // LOINC `ANSWER_LIST` properties may come back as a single semicolon-delimited string or as
  // multiple values. Either way, split on `;` and trim — empty entries are filtered out.
  const raw = Array.isArray(value) ? value : [value];
  const choices = raw
    .flatMap((v) => v.split(';'))
    .map((v) => v.trim())
    .filter((v) => v.length > 0);
  return choices.length > 0 ? choices : undefined;
};

export type LabLookupProps = {
  scaleType: LabScaleType;
  unit: string | undefined;
  ordinalChoices: string[] | undefined;
};

/**
 * Maps the LOINC `codeSystem.lookup` response shape into the props expected by `AddLabResultForm`.
 *
 * `parseLookupResponse` collapses repeated `Parameters.parameter` entries into an array, so any
 * property may arrive as a single `{ value }` entry or an array of those. `EXAMPLE_UCUM_UNITS`
 * values may additionally be semicolon-delimited.
 */
export const parseLabLookupProps = (properties: Record<string, LookupPropertyValue> | undefined): LabLookupProps => {
  const scaleRaw = unwrap(properties?.SCALE_TYP);
  const scaleKey = Array.isArray(scaleRaw) ? scaleRaw[0] : scaleRaw;
  const scaleType: LabScaleType = (scaleKey ? LOINC_SCALE_TO_FORM_SCALE[scaleKey] : undefined) ?? 'Qn';

  const unitRaw = unwrap(properties?.EXAMPLE_UCUM_UNITS);
  const firstUnit = Array.isArray(unitRaw) ? unitRaw[0] : unitRaw;
  const unit = typeof firstUnit === 'string' ? firstUnit.split(';')[0]?.trim() || undefined : undefined;

  const ordinalChoices = scaleType === 'Ord' ? parseOrdinalChoices(unwrap(properties?.ANSWER_LIST)) : undefined;

  return { scaleType, unit, ordinalChoices };
};
